import React, { useState, useEffect, useCallback } from 'react';
import { buscarEquipesPorCategoriaTorneio } from '../../../../services/equipeService';
import { vincularEquipeAoGrupo } from '../../../../services/faseGrupoPartidaEquipeService';
import { Equipe } from '../../../../types/equipe';

interface AddTeamToGroupModalProps {
  grupoId: string;
  faseCategoriaTorneioId: string;
  categoriaId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const AddTeamToGroupModal: React.FC<AddTeamToGroupModalProps> = ({
  grupoId,
  faseCategoriaTorneioId,
  categoriaId,
  onClose,
  onSuccess,
}) => {
  const [equipes, setEquipes] = useState<Equipe[]>([]);
  const [loading, setLoading] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const loadEquipes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await buscarEquipesPorCategoriaTorneio(categoriaId);
      setEquipes(data);
    } catch (error) {
      console.error('Erro ao carregar equipes:', error);
      alert('Erro ao carregar equipes');
    } finally {
      setLoading(false);
    }
  }, [categoriaId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.classList.add('modal-open');
    loadEquipes();
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('modal-open');
    };
  }, [loadEquipes, onClose]);

  const handleAddTeam = async (equipeId: string) => {
    setAddingId(equipeId);
    try {
      await vincularEquipeAoGrupo({
        faseGrupoPartidasId: grupoId,
        equipeId,
      });
      onSuccess();
    } catch (error) {
      console.error('Erro ao adicionar equipe:', error);
      alert('Erro ao adicionar equipe ao grupo');
    } finally {
      setAddingId(null);
    }
  };

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const filteredEquipes = equipes.filter((equipe) => {
    if (!searchTerm.trim()) return true;
    const search = searchTerm.toLowerCase();
    return (
      equipe.nome.toLowerCase().includes(search) ||
      equipe.atletas?.some((atleta) =>
        atleta.usuario?.nome.toLowerCase().includes(search),
      )
    );
  });

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div
        className="modal fade show d-block"
        role="dialog"
        tabIndex={-1}
        onMouseDown={handleBackdrop}
        style={{ overflow: 'auto' }}
      >
        <div
          className="modal-dialog modal-lg"
          role="document"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Adicionar Equipe ao Grupo</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Fechar"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar equipe..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              {loading ? (
                <div className="text-center py-3">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {filteredEquipes.length === 0 ? (
                    <p className="text-muted">Nenhuma equipe encontrada</p>
                  ) : (
                    filteredEquipes.map((equipe) => (
                      <div
                        key={equipe.id}
                        className="border rounded p-3 mb-2 d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <div className="fw-semibold">{equipe.nome}</div>
                          {equipe.atletas && equipe.atletas.length > 0 && (
                            <small className="text-muted">
                              {equipe.atletas
                                .map((a) => a.usuario?.nome || '')
                                .join(', ')}
                            </small>
                          )}
                        </div>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleAddTeam(equipe.id)}
                          disabled={addingId === equipe.id}
                        >
                          {addingId === equipe.id ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            'Adicionar'
                          )}
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddTeamToGroupModal;

